import { useState, useEffect, useRef } from "react";
import { LuPlus } from "react-icons/lu";
import API_URL from "../Config";
import "./Table.css";
import "./Equipment.css";
import "./SystemUsers.css";
import { useNavigate } from "react-router-dom";
import SearchBar from "../Components/SearchBar.js";
import PaginationFooter from "../Components/PaginationFooter.js";
import axiosInstance from "../axiosinstance";
import Tooltip from "@mui/material/Tooltip";
import { IoEye } from "react-icons/io5";
import ViewUserDetail from "../View/ViewUserDetail.js";
import { Typography, Box, Popover } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { IoIosAdd } from "react-icons/io";
function SystemUsers({ isSidebarExpanded }) {
  const [Users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const isInitialLoad = useRef(true);

  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [designationAnchorEl, setDesignationAnchorEl] = useState(null);
  const [designationPopoverItems, setDesignationPopoverItems] = useState([]);
  const [designationPopoverKey, setDesignationPopoverKey] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [equipmentGroupOptions, setEquipmentGroupOptions] = useState([]);

  useEffect(() => {
    const fetchEquipmentGroups = async () => {
      try {
        const res = await axiosInstance.get(`${API_URL}/api/get_all_equipment_groups`);
        const options = (res?.data?.data || []).map((item) => ({
          value: item.id,
          label: `${item.equipment_group_code || ""} - ${item.equipment_group_name || ""}`.trim(),
        }));
        setEquipmentGroupOptions(options);
      } catch (err) {
        console.error("Failed to fetch equipment groups:", err);
      }
    };

    fetchEquipmentGroups();
  }, []);

  useEffect(() => {
    if (isSearching && searchQuery) {
      fetchSearchUsers(searchQuery, currentPage);
    } else {
      fetchData(true, currentPage);
    }
  }, [currentPage]);

  const fetchSearchUsers = async (query, page = 1) => {
    try {
      const res = await axiosInstance.get(`${API_URL}/api/search_user`, {
        params: { query, page, Items_per_page: itemsPerPage },
      });
      const { Total_records } = res.data;
      setUsers(res.data.data || []);
      setTotalRecords(Total_records || 0);
    } catch (err) {
      console.error("Search fetch failed:", err.response?.data || err.message);
    }
  };

  const fetchData = async (retry = true, page = 1) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${API_URL}/api/get_all_system_user`, {
        params: {
          page,
          Items_per_page: itemsPerPage,
        },
      });
      const { Total_records } = response.data;
      setUsers(response.data.data || []);
      setTotalRecords(Total_records || 0);
    } catch (err) {
      console.error("Initial fetch failed:", err.response?.data || err.message);
      setErrorMessage("Failed to load users. Please refresh and try again.");
    } finally {
      setLoading(false);
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
      }
    }
  };

  const AddUser = () => {
    navigate("/User-Form");
  };

  const handleNavigate = (id) => {
    navigate(`/Edit-user-form/${id}`);
  };

  const getDesignationNames = (user) => {
    if (!Array.isArray(user?.assigned_departments)) return [];
    return user.assigned_departments
      .map((item) => item?.department_name)
      .filter(Boolean);
  };

  const openDesignationPopover = (event, designations, popoverKey) => {
    if (!designations.length) return;

    if (
      designationAnchorEl &&
      designationPopoverKey &&
      designationPopoverKey === popoverKey
    ) {
      closeDesignationPopover();
      return;
    }

    setDesignationAnchorEl(event.currentTarget);
    setDesignationPopoverItems(designations);
    setDesignationPopoverKey(popoverKey);
  };

  const closeDesignationPopover = () => {
    setDesignationAnchorEl(null);
    setDesignationPopoverItems([]);
    setDesignationPopoverKey(null);
  };

  const isDesignationPopoverOpen = Boolean(designationAnchorEl);

  return (
    <>
      <div>
        <div
          className={`table-container equipment-container equipment-page ${
            isSidebarExpanded ? "expanded" : "collapsed"
          }`}
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div className="equipment-page-content">
            {/* Header */}
            <div className="equipment-page-header mt-2">
              <div className="equipment-header-titleblock">
                <h2 className="page-title mb-0">System Users</h2>
              </div>
              <div className="equipment-header-buttons d-flex align-items-center">
                <div className="equipment-header-controls-row">
                  <div className="equipment-search-inline">
                    <SearchBar
                      placeholder="Search System Users..."
                      apiEndpoint={`${API_URL}/api/search_user`}
                      resultKey="data"
                      onResults={({ data, totalRecords, isSearch, query }) => {
                        setUsers(data);
                        if (typeof totalRecords !== "undefined")
                          setTotalRecords(totalRecords);
                        setIsSearching(isSearch);
                        setSearchQuery(query);
                        setCurrentPage(1);
                      }}
                      fetchDefaultData={() => fetchData()}
                    />
                  </div>
                  <button
                    className="import-button equipment-import-button area-filter-clear-btn"
                    onClick={AddUser}
                  >
                    <IoIosAdd size={16} />
                    New
                  </button>
                </div>
              </div>
            </div>

            {errorMessage && (
              <div className="equipment-error-message">
                <strong>Error:</strong> {errorMessage}
              </div>
            )}

            <div className="equipment-table-card">
              <div
                className="equipment-table-wrapper"
                style={{ flex: 1, minHeight: 0 }}
              >
                <table className="equipment-data-table system-users-table">
                  <colgroup>
                    <col className="users-col-sr" />
                    <col className="users-col-name" />
                    <col className="users-col-email" />
                    <col className="users-col-contact" />
                    <col className="users-col-group" />
                    <col className="users-col-designation" />
                    <col className="users-col-role" />
                    <col className="users-col-actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Sr. No</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Contact Number</th>
                      <th>Assigned Group</th>
                      <th>Designations</th>
                      <th>Role</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 10 }).map((_, rowIdx) => (
                        <tr
                          key={`user-skeleton-${rowIdx}`}
                          className="equipment-skeleton-row"
                        >
                          {Array.from({ length: 8 }).map((_, colIdx) => (
                            <td key={colIdx}>
                              <span className="equipment-skeleton-bar" />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : Users.length > 0 ? (
                      Users.map((user, index) => (
                        <tr key={user.user_id || index}>
                          <td>{user.serial_number || index + 1}</td>
                          <td title={user.users_name || "N/A"}>
                            {user.users_name || "N/A"}
                          </td>
                          <td title={user.users_email || "N/A"}>
                            {user.users_email || "N/A"}
                          </td>
                          <td>{user.contact_number || "N/A"}</td>
                          <td>
                            {user.assigned_group && user.assigned_group.length > 0
                              ? user.assigned_group
                                  .map((g) => g.group_name)
                                  .join(", ")
                              : "No Groups Assigned"}
                          </td>
                          <td>
                            {(() => {
                              const designationNames = getDesignationNames(user);
                              if (designationNames.length === 0) return "N/A";
                              const firstDesignation = designationNames[0];
                              const remainingCount = designationNames.length - 1;
                              return (
                                <div className="users-designation-cell">
                                  <span
                                    className={`users-designation-text${
                                      remainingCount > 0
                                        ? " users-designation-text-short"
                                        : ""
                                    }`}
                                  >
                                    {firstDesignation}
                                  </span>
                                  {remainingCount > 0 && (
                                    <button
                                      type="button"
                                      className="users-designation-more"
                                      onClick={(e) =>
                                        openDesignationPopover(
                                          e,
                                          designationNames,
                                          `${user.user_id || index}`,
                                        )
                                      }
                                    >
                                      +{remainingCount}
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td>{user.role || "N/A"}</td>
                          <td>
                            <div className="equipment-actions-inline">
                              <Tooltip title="Edit">
                                <button
                                  type="button"
                                  className="equipment-action-icon-btn"
                                  onClick={() => handleNavigate(user.user_id)}
                                >
                                  <EditIcon fontSize="small" />
                                </button>
                              </Tooltip>
                              <Tooltip title="View">
                                <button
                                  type="button"
                                  className="equipment-action-icon-btn"
                                  onClick={() => {
                                    setSelectedUserId(user.user_id);
                                    setViewModalOpen(true);
                                  }}
                                >
                                  <IoEye style={{ fontSize: "19px" }} />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={8}
                          style={{
                            textAlign: "center",
                            padding: "32px 0",
                            color: "#6b7280",
                          }}
                        >
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="equipment-pagination-footer">
                <PaginationFooter
                  totalRecords={totalRecords}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  setCurrentPage={setCurrentPage}
                  setItemsPerPage={setItemsPerPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ViewUserDetail
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        equipmentGroupOptions={equipmentGroupOptions}
      />

      <Popover
        open={isDesignationPopoverOpen}
        anchorEl={designationAnchorEl}
        onClose={closeDesignationPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableRestoreFocus
        PaperProps={{
          sx: {
            mt: 0.5,
            p: 1,
            borderRadius: 1,
            border: "1px solid #e7e7e7",
            boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
            backgroundColor: "#fff",
            minWidth: 180,
            maxWidth: 280,
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {designationPopoverItems.map((name, idx) => (
            <Typography
              key={`${name}-${idx}`}
              variant="body2"
              sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
            >
              {name}
            </Typography>
          ))}
        </Box>
      </Popover>
    </>
  );
}

export default SystemUsers;
